/**
 * Token Manager Service
 *
 * Manages OAuth token lifecycle including storage, validation, expiration tracking,
 * and refresh handling. Provides pre-expiration callbacks for proactive token renewal.
 *
 * @module services/tokenManager
 */

// Storage keys
const GITHUB_TOKEN_KEY = 'portfolistic_github_token';
const TOKEN_EXPIRY_KEY = 'portfolistic_github_token_expiry';
const TOKEN_METADATA_KEY = 'portfolistic_github_token_metadata';

// Pre-expiry warning time (5 minutes before expiration)
const PRE_EXPIRY_WARNING_MS = 5 * 60 * 1000;

// Minimum validity for token to be considered usable (1 minute)
const MIN_VALIDITY_MS = 60 * 1000;

// Callbacks registered for token expiry warnings
let expiryCallbacks = [];

// Interval ID for expiry monitoring
let expiryMonitorId = null;

/**
 * Store a GitHub OAuth token with expiration tracking.
 *
 * @param {string} token - The OAuth access token
 * @param {Date|number|null} expiresAt - Token expiration time (Date object, timestamp, or null for no expiry)
 * @param {Object} metadata - Optional metadata about the token
 */
export const storeToken = (token, expiresAt = null, metadata = {}) => {
  if (!token) {
    console.warn('Attempted to store empty token');
    return;
  }

  try {
    sessionStorage.setItem(GITHUB_TOKEN_KEY, token);

    if (expiresAt) {
      const expiryTimestamp = expiresAt instanceof Date ? expiresAt.getTime() : expiresAt;
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTimestamp));
    } else {
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    }

    if (Object.keys(metadata).length > 0) {
      sessionStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify({
        ...metadata,
        storedAt: Date.now(),
      }));
    }

    // Start expiry monitoring if we have an expiration time
    if (expiresAt) {
      startExpiryMonitor();
    }
  } catch (error) {
    console.error('Failed to store token:', error);
  }
};

/**
 * Get the stored GitHub OAuth token.
 * Returns null if no token exists or if the token is expired.
 *
 * @returns {string|null} The valid token or null
 */
export const getToken = () => {
  try {
    const token = sessionStorage.getItem(GITHUB_TOKEN_KEY);

    if (!token) {
      return null;
    }

    // Check if token is expired
    const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      if (Date.now() >= expiry) {
        // Token is expired, clear it
        clearToken();
        return null;
      }
    }

    return token;
  } catch (error) {
    console.error('Failed to get token:', error);
    return null;
  }
};

/**
 * Check if the stored token is valid (exists and not expired).
 * Optionally validates against GitHub API.
 *
 * @param {Object} options - Validation options
 * @param {boolean} options.validateWithApi - If true, validates token with GitHub API
 * @param {Function} options.apiValidator - Function that validates token with API (receives token, returns boolean)
 * @returns {Promise<boolean>} True if token is valid
 */
export const isTokenValid = async ({ validateWithApi = false, apiValidator = null } = {}) => {
  const token = getToken();

  if (!token) {
    return false;
  }

  // Check minimum remaining validity
  const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (expiryStr) {
    const expiry = parseInt(expiryStr, 10);
    const remaining = expiry - Date.now();
    if (remaining < MIN_VALIDITY_MS) {
      return false;
    }
  }

  // Optionally validate with API
  if (validateWithApi && apiValidator) {
    try {
      return await apiValidator(token);
    } catch (error) {
      console.error('Token API validation failed:', error);
      return false;
    }
  }

  return true;
};

/**
 * Get time remaining until token expires.
 *
 * @returns {number|null} Milliseconds until expiry, null if no expiry set, 0 if expired
 */
export const getTimeUntilExpiry = () => {
  try {
    const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) {
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    const remaining = expiry - Date.now();
    return Math.max(0, remaining);
  } catch (error) {
    return null;
  }
};

/**
 * Get the token expiration timestamp.
 *
 * @returns {number|null} Expiration timestamp in milliseconds, or null if not set
 */
export const getTokenExpiry = () => {
  try {
    const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) {
      return null;
    }
    return parseInt(expiryStr, 10);
  } catch (error) {
    return null;
  }
};

/**
 * Get stored token metadata.
 *
 * @returns {Object|null} Token metadata or null
 */
export const getTokenMetadata = () => {
  try {
    const metadataStr = sessionStorage.getItem(TOKEN_METADATA_KEY);
    if (!metadataStr) {
      return null;
    }
    return JSON.parse(metadataStr);
  } catch (error) {
    console.error('Failed to parse token metadata:', error);
    return null;
  }
};

/**
 * Clear the stored token and related data.
 */
export const clearToken = () => {
  try {
    sessionStorage.removeItem(GITHUB_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(TOKEN_METADATA_KEY);
    stopExpiryMonitor();
  } catch (error) {
    console.error('Failed to clear token:', error);
  }
};

/**
 * Trigger a token refresh by redirecting to OAuth flow.
 * This function should be called when the token is expired or about to expire.
 *
 * @param {Object} options - Refresh options
 * @param {Function} options.onRefreshNeeded - Callback when refresh is needed (e.g., redirect to OAuth)
 * @param {string} options.reason - Reason for refresh ('expired', 'expiring', 'invalid')
 */
export const refreshToken = ({ onRefreshNeeded, reason = 'expired' } = {}) => {
  // Clear current token
  clearToken();

  // Trigger refresh callback
  if (onRefreshNeeded) {
    onRefreshNeeded({ reason });
  } else {
    console.warn('Token refresh needed but no callback provided. Reason:', reason);
  }
};

/**
 * Register a callback to be called when token is about to expire.
 * Callbacks are called ~5 minutes before expiration.
 *
 * @param {Function} callback - Function to call with { expiresIn: ms, token: string }
 * @returns {Function} Unsubscribe function
 */
export const onTokenExpiring = (callback) => {
  if (typeof callback !== 'function') {
    throw new Error('Callback must be a function');
  }

  expiryCallbacks.push(callback);

  // Start monitoring if we have callbacks and an expiry time
  if (getTokenExpiry()) {
    startExpiryMonitor();
  }

  // Return unsubscribe function
  return () => {
    expiryCallbacks = expiryCallbacks.filter(cb => cb !== callback);
    if (expiryCallbacks.length === 0) {
      stopExpiryMonitor();
    }
  };
};

/**
 * Start monitoring token expiration.
 */
const startExpiryMonitor = () => {
  if (expiryMonitorId) {
    return; // Already monitoring
  }

  // Check every 30 seconds
  expiryMonitorId = setInterval(() => {
    const timeUntilExpiry = getTimeUntilExpiry();

    if (timeUntilExpiry === null) {
      return; // No expiry set
    }

    if (timeUntilExpiry === 0) {
      // Token expired
      notifyExpiryCallbacks({ expired: true, expiresIn: 0 });
      stopExpiryMonitor();
      return;
    }

    if (timeUntilExpiry <= PRE_EXPIRY_WARNING_MS) {
      // Token expiring soon
      notifyExpiryCallbacks({ expired: false, expiresIn: timeUntilExpiry });
    }
  }, 30000);
};

/**
 * Stop monitoring token expiration.
 */
const stopExpiryMonitor = () => {
  if (expiryMonitorId) {
    clearInterval(expiryMonitorId);
    expiryMonitorId = null;
  }
};

/**
 * Notify all registered callbacks about token expiration.
 */
const notifyExpiryCallbacks = ({ expired, expiresIn }) => {
  const token = getToken();
  expiryCallbacks.forEach(callback => {
    try {
      callback({ expired, expiresIn, token });
    } catch (error) {
      console.error('Error in token expiry callback:', error);
    }
  });
};

/**
 * Get current token status.
 *
 * @returns {Object} Token status object
 */
export const getTokenStatus = () => {
  const token = getToken();
  const expiry = getTokenExpiry();
  const timeUntilExpiry = getTimeUntilExpiry();
  const metadata = getTokenMetadata();

  return {
    hasToken: !!token,
    isValid: !!token && (timeUntilExpiry === null || timeUntilExpiry > MIN_VALIDITY_MS),
    expiresAt: expiry ? new Date(expiry) : null,
    expiresIn: timeUntilExpiry,
    isExpiringSoon: timeUntilExpiry !== null && timeUntilExpiry <= PRE_EXPIRY_WARNING_MS && timeUntilExpiry > 0,
    isExpired: timeUntilExpiry === 0,
    metadata,
  };
};

/**
 * React hook for token status.
 * Call this in a component to get reactive token status.
 *
 * Note: This returns a function that gets current status.
 * For reactive updates, combine with onTokenExpiring callback.
 *
 * @returns {Object} Object with getStatus function and subscribe method
 */
export const useTokenStatus = () => {
  return {
    getStatus: getTokenStatus,
    subscribe: onTokenExpiring,
    refresh: refreshToken,
  };
};

export default {
  storeToken,
  getToken,
  isTokenValid,
  getTimeUntilExpiry,
  getTokenExpiry,
  getTokenMetadata,
  clearToken,
  refreshToken,
  onTokenExpiring,
  getTokenStatus,
  useTokenStatus,
};
